/**
 * stockSync.ts — Sistema de sincronización de stock en tiempo real.
 *
 * QUÉ HACE:
 *   Emite y escucha eventos de cambio en el inventario para que Caja,
 *   Inventario y Tienda se mantengan sincronizados sin recargar la página.
 *   Usa CustomEvent del DOM para comunicación intra-tab y el evento
 *   nativo `storage` de Window para comunicación entre tabs del mismo origen.
 *
 * QUÉ EXPORTA:
 *   - emitStockChange()    → dispara la señal de actualización
 *   - useStockSync(cb)     → hook que ejecuta `cb` al recibir la señal
 *
 * QUÉ NO DEBE HACER:
 *   - No debe contener lógica de UI ni componentes React.
 *   - No debe hacer llamadas API (solo coordina la señal).
 */

import { useEffect } from "react";

const STOCK_CHANGE_EVENT = "altenot:stock-change";
const STORAGE_KEY = "altenot_stock_version";

/**
 * Emite una señal de cambio de inventario.
 * Los hooks de Caja, Inventario y Tienda la escucharán para refrescar sus datos.
 */
export const emitStockChange = () => {
  // Intra-tab: CustomEvent para que los hooks del mismo tab reaccionen
  window.dispatchEvent(new CustomEvent(STOCK_CHANGE_EVENT));

  // Cross-tab: cambiar un valor en localStorage genera el evento `storage` en otras tabs
  localStorage.setItem(STORAGE_KEY, Date.now().toString());
};

/**
 * Hook que llama `onStockChange` cada vez que se detecta un cambio de inventario,
 * ya sea dentro del mismo tab (CustomEvent) o desde otro tab (storage event).
 */
export const useStockSync = (onStockChange: () => void) => {
  useEffect(() => {
    // Intra-tab
    const handleCustom = () => onStockChange();
    window.addEventListener(STOCK_CHANGE_EVENT, handleCustom);

    // Cross-tab (solo se dispara cuando OTRO tab cambia localStorage)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) onStockChange();
    };
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(STOCK_CHANGE_EVENT, handleCustom);
      window.removeEventListener("storage", handleStorage);
    };
  }, [onStockChange]);
};
